import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import BrokenImageIcon from '@material-ui/icons/BrokenImage'
import { Base64 } from 'js-base64'
import { md5 } from 'js-md5'
import { Img as ReactImage } from 'react-image'
import { connect } from 'react-redux'

import type { ClassNameMap } from '@material-ui/core/styles/withStyles'
import type { ImgProps } from 'react-image'

import PoziAPIData from 'api/data'
import { getURLLabel } from 'drawer/tabs/info/panels/ContentType'

import Loading from './Loading'

const styles = {
  root: {},
  brokenImageIcon: {
    filter: 'invert(51%) sepia(0%) saturate(1849%) hue-rotate(208deg) brightness(93%) contrast(85%)' // https://codepen.io/sosuke/pen/Pjoqqp
  },
  image: {
    maxWidth: '85%'
    /* DISABLE ANIMATION BELOW FOR THE TIME BEING */
    // width: 0,
    // height: 0,
    // animation: '$growAndFadeIn 1000ms',
    // animationFillMode: 'forwards',
    // animationTimingFunction: 'ease-in-out'
  },
  '@keyframes growAndFadeIn': {
    '0%': {
      opacity: 0,
      width: 0,
      height: 0
    },
    '50%': {
      opacity: 0,
      width: 'auto',
      height: '200px'
    },
    '100%': {
      opacity: 1,
      width: 'auto',
      height: 'auto'
    }
  }
}

const imgCache: Record<string, string> = {}

// Detect inline data images
export const inlineDataImgRegExp = /^data:image\/(gif|png|jpg);base64,/

// Use the following signatures to detect valid inline images
// From: https://stackoverflow.com/a/58158656
const base64MimeTypeSignatures = {
  // JVBERi0: 'application/pdf',
  R0lGOD: 'image/gif',
  iVBORw0KGgo: 'image/png',
  '/9j/': 'image/jpg'
}

export const isBase64Image = (url: string) => {
  // Check if it is valid base64 (not sufficient by itself due to false positives)
  if (!Base64.isValid(url)) {
    return false
  }

  // Even valid
  const base64Signatures = Object.keys(base64MimeTypeSignatures)
  const isImage = base64Signatures.some(signature => url.startsWith(signature))
  if (!isImage) {
    return false
  }

  return true
}

export const getBase64MimeType = (str: string) => {
  for (const [key, value] of Object.entries(base64MimeTypeSignatures)) {
    if (str.startsWith(key)) {
      return value
    }
  }

  return null
}

export const isInlineDataImage = (url: string) => inlineDataImgRegExp.test(url)

export const loadImage = (url: string, options?: Record<string, any>) => {
  let ajaxOptions = {
    method: 'GET',
    url,
    data: '',
    dataType: 'binary',
    xhrFields: {
      responseType: 'blob'
    },

    // Allow passed options to override and add to the ajaxOptions
    ...options
  }

  // Some servers cannot handle very long URLs: we convert the request
  // to a POST automatically.
  // CloudFront, for example, has a limit of 8,192 bytes.
  if (url.length > 8000) {
    const urlObject = new URL(url)
    const newUrl = urlObject.origin + urlObject.pathname
    const data = urlObject.search.replace(/^\?/, '') // remove the '?' at the start

    ajaxOptions = {
      ...ajaxOptions,
      method: 'POST',
      url: newUrl,
      data
    }
  }

  return PoziAPIData().ajax(ajaxOptions)
}

class Image_ extends React.Component<ImageProps, ImageState> {
  constructor (props: ImageProps) {
    super(props)

    this.state = {
      imgObjUrl: undefined,
      status: undefined
    }
  }

  componentDidMount () {
    // console.debug('Image - componentDidMount', this.props)

    const { src } = this.props

    this.getSrc(src)
  }

  componentDidUpdate (prevProps: ImageProps) {
    // console.debug('ChildPanel - componentDidUpdate')

    const { src } = this.props

    const srcChanged = src !== prevProps.src

    // console.debug('Image - src changed?', srcChanged, src, prevProps.src)

    if (!srcChanged) {
      return
    }

    this.getSrc(src)
  }

  getSrc = async (src: string) => {
    // console.debug('Image - getSrc', src)

    if (!src) {
      // console.debug('Image - getSrc - no src', src)
      return
    }

    // Create hash of the URL for the key as the URL can get quite large
    // when inline URLs are used
    const srcHash = md5(src)

    // check if image is in cache already
    const imgObjUrl = imgCache[srcHash]
    if (imgObjUrl) {
      this.setState({
        status: 'loaded',
        imgObjUrl
      })

      return
    }

    // try to load
    try {
      this.setState({
        status: 'loading'
      })

      // await PoziUtils.sleep(10000) // for debugging

      let imgObjUrl

      // If image is base64 encoded, use it directly, otherwise load it
      // and convert to a blob
      if (isInlineDataImage(src)) {
        imgObjUrl = src
      } else if (isBase64Image(src)) {
        const mimeType = getBase64MimeType(src) || 'image/png'
        imgObjUrl = `data:${mimeType};base64,${src}` // make it a data URL (mime type does not really matter)
      } else {
        const imgBlob = await loadImage(src)
        imgObjUrl = URL.createObjectURL(imgBlob)
      }

      // store image in cache
      imgCache[srcHash] = imgObjUrl

      this.setState({
        imgObjUrl,
        status: 'loaded'
      })
    } catch (error) {
      console.log('Image - loadImage error', error, src)

      this.setState({
        imgObjUrl: null,
        status: 'error'
      })
    }
  }

  render () {
    const { classes, src, ...userOptions } = this.props

    const { imgObjUrl, status } = this.state

    // console.debug('Image - render', status, src, imgObjUrl)

    // Set the image source to the URL string initially. We may override this
    // once the image has loaded successfully.
    let imgSrc = src

    const brokenImageIcon = <BrokenImageIcon className={classes.brokenImageIcon} />
    const loadingPlaceHolder = <Loading text="" />

    // Don't render anything if status is uninitialised
    if (!status) {
      return null
    }

    // Show a loading spinner while we fetch the image
    if (status === 'loading') {
      return loadingPlaceHolder
    }

    // If the image has successfully been fetched, use the image object
    if (status === 'loaded') {
      imgSrc = imgObjUrl
    }

    // If status is either 'loaded' or 'error', we will display the image component.
    // The error could have occurred due to the server not allowing the image to be loaded
    // using CORS.

    const imageOptions = {
      loader: loadingPlaceHolder,
      unloader: brokenImageIcon,
      alt: getURLLabel(src),

      ...userOptions
    }

    return (
      <ReactImage className={classes.image} src={imgSrc} { ...imageOptions } />
    )
  }
}

function mapStateToProps (state: ImageState, ownProps: ImageProps) {
  return {}
}

const mapDispatchToProps = () => {
  return {}
}

const Image = (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Image_)
  )
)

export default Image

interface ImageProps extends ImgProps {
  classes: Partial<ClassNameMap<keyof typeof styles>>
  src: string // we will only deal with single sources (not arrays)
}

interface ImageState {
  imgObjUrl: any,
  status: 'error' | 'loaded' | 'loading' | undefined
}
